import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, ExternalLink, Video } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, videoEmbedUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

export default function VideoSlots() {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const load = () => api.get("/admin/video-slots").then((r) => { setData(r.data); setDrafts(Object.fromEntries(r.data.slots.map((s) => [s.step_id, s.video_url || ""]))); }).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const save = async (slot, url) => {
    if (url && !videoEmbedUrl(url)) { toast.error("Paste a YouTube or Vimeo link"); return; }
    try {
      await api.put(`/admin/video-slots/${slot.tutorial_id}/${slot.step_id}`, { video_url: url || null });
      toast.success(url ? "Video saved" : "Video removed");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14" data-testid="video-slots-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-700 mb-6" data-testid="videos-back-link"><ArrowLeft className="w-4 h-4" /> Back to tutorials</Link>
      <p className="eyebrow">Admin panel</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-2">Demonstration videos</h1>
      <p className="text-slate-600 mt-2 mb-8 max-w-2xl">Record a role-play for each clinician guide, upload it to YouTube (unlisted is fine) or Vimeo, and paste the link here. The video appears above the written script in that step.</p>

      {!data ? <Skeleton className="h-64 rounded-3xl" /> : (
        <>
          <div className="rounded-3xl bg-slate-900 text-white p-6 mb-6 flex items-center gap-5" data-testid="video-progress">
            <div className="font-display text-4xl font-extrabold">{data.done}<span className="text-slate-400 text-2xl">/{data.total}</span></div>
            <div className="flex-1">
              <p className="font-semibold">videos added</p>
              <div className="h-2 bg-white/15 rounded-full mt-2 overflow-hidden"><div className="h-full bg-emerald-400 transition-all" style={{ width: `${data.total ? (data.done / data.total) * 100 : 0}%` }} /></div>
            </div>
          </div>
          <ul className="space-y-4" data-testid="video-slots-list">
            {data.slots.map((slot) => (
              <li key={slot.step_id} className="rounded-3xl bg-white border border-slate-200 p-5 sm:p-6" data-testid={`video-slot-${slot.step_id}`}>
                <div className="flex items-start gap-3">
                  {slot.done ? <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" /> : <Circle className="w-6 h-6 text-slate-300 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-slate-900">{slot.tutorial_title}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1"><Video className="w-3.5 h-3.5" /> {slot.step_title} · <Link to={`/tutorials/${slot.tutorial_id}`} className="text-indigo-600 hover:underline inline-flex items-center gap-1">view step <ExternalLink className="w-3 h-3" /></Link></p>
                    <form onSubmit={(e) => { e.preventDefault(); save(slot, drafts[slot.step_id].trim()); }} className="mt-3 flex flex-col sm:flex-row gap-2">
                      <Input value={drafts[slot.step_id] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [slot.step_id]: e.target.value }))} placeholder="https://www.youtube.com/watch?v=… or https://vimeo.com/…" className="rounded-xl" data-testid={`video-url-${slot.step_id}`} />
                      <Button type="submit" className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`video-save-${slot.step_id}`}>Save</Button>
                      {slot.done && <Button type="button" variant="ghost" className="rounded-xl text-rose-600" onClick={() => save(slot, "")} data-testid={`video-remove-${slot.step_id}`}>Remove</Button>}
                    </form>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
